import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { BriefcaseBusiness, Plus, ShieldAlert, User, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";
import { useAppAuth } from "@/auth/AppAuthProvider";
import { useTerminal } from "@/context/TerminalContext";

const csv = value => String(value || "").split(",").map(item => item.trim()).filter(Boolean);
const caseDraft = () => ({
  title: "", case_type: "restriction", summary: "", details: "", message: "", severity: "warning",
  required_action: "", agreement_id: "", source_reference: "",
  effective_from: "", effective_to: "", confidential: true,
});

export default function Personnel() {
  const { hasCapability } = useAppAuth();
  const { currentTerminal, currentTerminalId } = useTerminal();
  const canManageReference = hasCapability("reference.manage");
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [terms, setTerms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [caseForm, setCaseForm] = useState(caseDraft);
  const [caseParent, setCaseParent] = useState(null);
  const [form, setForm] = useState({
    terminal_id: "", name: "", role: "operator", department: "", certifications: "", zone_auth: "",
    status: "available", employment_group: "union_longshore", labor_group_id: "", labor_classifications: "",
    ratings: "", employer: "", seniority_date: "", dispatch_identifier: "", regular_workforce: false,
  });
  const [groupForm, setGroupForm] = useState({
    terminal_id: "", name: "", union_name: "", local_name: "", bargaining_unit: "",
    employer: "", dispatch_source: "", active: true,
  });

  const refresh = useCallback(async () => {
    const [people, terminals, laborGroups] = await Promise.all([
      api.list("personnel"), api.list("terminals"), api.list("labor-groups"),
    ]);
    setList(people); setTerms(terminals); setGroups(laborGroups);
    const defaultTerminal = currentTerminalId || terminals[0]?.id || "";
    setForm(current => !current.terminal_id ? { ...current, terminal_id: defaultTerminal } : current);
    setGroupForm(current => !current.terminal_id ? { ...current, terminal_id: defaultTerminal } : current);
  }, [currentTerminalId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!currentTerminalId) return;
    if (!open) setForm(current => ({ ...current, terminal_id: currentTerminalId }));
    if (!groupsOpen) setGroupForm(current => ({ ...current, terminal_id: currentTerminalId }));
  }, [currentTerminalId, open, groupsOpen]);
  const visiblePeople = currentTerminalId ? list.filter(person => person.terminal_id === currentTerminalId) : list;
  const visibleGroups = currentTerminalId ? groups.filter(group => !group.terminal_id || group.terminal_id === currentTerminalId) : groups;

  const groupById = useMemo(
    () => Object.fromEntries(groups.map(group => [group.id, group])),
    [groups],
  );

  const create = async () => {
    if (!form.name || !form.terminal_id) return toast.error("Name and terminal are required.");
    await api.create("personnel", {
      ...form,
      labor_group_id: form.labor_group_id || null,
      certifications: csv(form.certifications),
      zone_auth: csv(form.zone_auth),
      labor_classifications: csv(form.labor_classifications),
      ratings: csv(form.ratings),
      seniority_date: form.seniority_date || null,
    });
    setOpen(false);
    toast.success("Personnel added");
    refresh();
  };

  const createGroup = async () => {
    if (!groupForm.name) return toast.error("Group name is required.");
    await api.create("labor-groups", { ...groupForm, terminal_id: groupForm.terminal_id || null });
    setGroupForm(current => ({ ...current, name: "", union_name: "", local_name: "", bargaining_unit: "", employer: "", dispatch_source: "" }));
    toast.success("Labour group created");
    refresh();
  };

  const deactivateGroup = async group => {
    if (!window.confirm(`Deactivate ${group.name}? Linked union workers will be treated as having unconfigured coverage until reassigned.`)) return;
    await api.deactivateLaborGroup(group.id);
    toast.success("Labour group deactivated");
    refresh();
  };

  const toggleStatus = async person => {
    const next = person.status === "available" ? "unavailable" : "available";
    await api.patch("personnel", person.id, { status: next }, person.revision);
    refresh();
  };

  const openProfile = async person => {
    setSelected({
      ...person,
      labor_group_id: person.labor_group_id || "",
      labor_classifications: (person.labor_classifications || []).join(", "),
      ratings: (person.ratings || []).join(", "),
      seniority_date: person.seniority_date || "",
    });
    setSummary(null);
    try {
      setSummary(await api.personnelLaborSummary(person.id));
    } catch {
      toast.error("Could not load the labour history.");
    }
  };

  useEffect(() => {
    const personId = searchParams.get("person");
    const person = list.find(item => item.id === personId);
    if (person && selected?.id !== personId) {
      openProfile(person);
      setSearchParams({}, { replace: true });
    }
  }, [list, searchParams, selected?.id, setSearchParams]);

  const saveProfile = async () => {
    const updated = await api.patch("personnel", selected.id, {
      employment_group: selected.employment_group,
      labor_group_id: selected.labor_group_id || null,
      labor_classifications: csv(selected.labor_classifications),
      ratings: csv(selected.ratings),
      employer: selected.employer || "",
      seniority_date: selected.seniority_date || null,
      dispatch_identifier: selected.dispatch_identifier || "",
      regular_workforce: Boolean(selected.regular_workforce),
    }, selected.revision);
    setSelected(current => ({ ...current, ...updated }));
    toast.success("Labour profile saved");
    await refresh();
    setSummary(await api.personnelLaborSummary(selected.id));
  };

  const createCase = async () => {
    if (!caseForm.title) return toast.error("Case title is required.");
    const { source_reference, ...caseValues } = caseForm;
    const payload = {
      labor_group_id: caseParent?.labor_group_id || selected.labor_group_id || null,
      agreement_id: caseForm.agreement_id || caseParent?.agreement_id || null,
      component_id: caseParent?.component_id || null,
      related_rule_ids: caseParent?.related_rule_ids || [],
      outcome: "",
      condition: caseParent?.condition || {},
      source: source_reference ? { ...(caseParent?.source || {}), reference: source_reference } : (caseParent?.source || {}),
      ...caseValues,
      personnel_id: selected.id,
      effective_from: caseForm.effective_from || null,
      effective_to: caseForm.effective_to || null,
    };
    if (caseParent) await api.versionWorkerCase(caseParent.id, payload, caseParent.revision);
    else await api.createWorkerCase(selected.id, payload);
    setCaseForm(caseDraft());
    setCaseParent(null);
    setSummary(await api.personnelLaborSummary(selected.id));
    toast.success(caseParent ? "New case version saved for review; the active version is unchanged." : "Case saved for review; it is not active.");
  };

  const reviseCase = caseItem => {
    setCaseParent(caseItem);
    setCaseForm({
      title: caseItem.title || "",
      case_type: caseItem.case_type || "other",
      summary: caseItem.summary || "",
      details: caseItem.details || "",
      message: caseItem.message || "",
      severity: caseItem.severity || "warning",
      required_action: caseItem.required_action || "",
      agreement_id: caseItem.agreement_id || "",
      source_reference: caseItem.source?.reference || "",
      effective_from: (caseItem.effective_from || "").slice(0, 16),
      effective_to: (caseItem.effective_to || "").slice(0, 16),
      confidential: caseItem.confidential !== false,
    });
  };

  const activateCase = async caseItem => {
    await api.activateWorkerCase(caseItem.id, {});
    setSummary(await api.personnelLaborSummary(selected.id));
    toast.success("Case activated and will now be enforced.");
  };

  const resolveCase = async caseItem => {
    const outcome = window.prompt("Resolution or outcome");
    if (!outcome) return;
    await api.resolveWorkerCase(caseItem.id, { outcome });
    setSummary(await api.personnelLaborSummary(selected.id));
    toast.success("Case resolved");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Workforce</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-slate-50">Personnel</h1>
          <p className="text-sm text-slate-500 mt-1">Labour coverage for {currentTerminal?.name || "the current terminal"}. Confidential case details remain permission controlled.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={groupsOpen} onOpenChange={setGroupsOpen}>
            {canManageReference && <DialogTrigger asChild>
              <Button variant="outline" className="border-[#1E293B] rounded-none text-slate-300"><Users size={14} className="mr-1.5"/>Labour groups</Button>
            </DialogTrigger>}
            <DialogContent className="bg-[#0E1522] border-[#1E293B] rounded-none text-slate-100 max-w-2xl">
              <DialogHeader><DialogTitle>Labour groups</DialogTitle></DialogHeader>
              <p className="text-xs text-slate-400">Reusable union, bargaining-unit, employer, and dispatch coverage. Link workers and agreements to the same group.</p>
              <div className="max-h-36 overflow-y-auto border border-[#1E293B]">
                {visibleGroups.map(group => <div key={group.id} className="flex justify-between gap-3 border-b border-[#1E293B] px-3 py-2 text-sm">
                  <span>{group.name}<span className="ml-2 text-xs text-slate-500">{group.local_name || group.union_name}</span></span>
                  <span className={group.active ? "text-emerald-400" : "text-amber-400"}>{group.active ? <button onClick={() => deactivateGroup(group)} className="hover:text-amber-300">Active · deactivate</button> : "Inactive"}</span>
                </div>)}
                {!visibleGroups.length && <p className="p-3 text-xs text-slate-500">No labour groups configured.</p>}
              </div>
              <details className="border border-[#1E293B] p-3">
                <summary className="cursor-pointer text-sm text-sky-300">Create a labour group</summary>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Field label="Terminal"><TerminalSelect value={groupForm.terminal_id} terms={terms} onChange={value => setGroupForm({...groupForm, terminal_id: value})}/></Field>
                  <Field label="Group name"><DarkInput value={groupForm.name} onChange={e => setGroupForm({...groupForm, name: e.target.value})}/></Field>
                  <Field label="Union"><DarkInput value={groupForm.union_name} onChange={e => setGroupForm({...groupForm, union_name: e.target.value})}/></Field>
                  <Field label="Local"><DarkInput value={groupForm.local_name} onChange={e => setGroupForm({...groupForm, local_name: e.target.value})}/></Field>
                  <Field label="Bargaining unit"><DarkInput value={groupForm.bargaining_unit} onChange={e => setGroupForm({...groupForm, bargaining_unit: e.target.value})}/></Field>
                  <Field label="Employer"><DarkInput value={groupForm.employer} onChange={e => setGroupForm({...groupForm, employer: e.target.value})}/></Field>
                  <Field label="Dispatch source" className="col-span-2"><DarkInput value={groupForm.dispatch_source} onChange={e => setGroupForm({...groupForm, dispatch_source: e.target.value})}/></Field>
                </div>
                <Button onClick={createGroup} size="sm" className="mt-3 bg-sky-600 rounded-none">Create group</Button>
              </details>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            {canManageReference && <DialogTrigger asChild>
              <Button data-testid={TESTIDS.btnCreatePersonnel} className="bg-sky-600 hover:bg-sky-500 rounded-none"><Plus size={14} className="mr-1.5" />New personnel</Button>
            </DialogTrigger>}
            <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">New personnel</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Terminal"><TerminalSelect value={form.terminal_id} terms={terms} onChange={value => setForm({...form, terminal_id: value})}/></Field>
                <Field label="Name"><DarkInput data-testid="input-personnel-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}/></Field>
                <Field label="Role"><DarkInput value={form.role} onChange={e => setForm({...form, role: e.target.value})}/></Field>
                <Field label="Department"><DarkInput value={form.department} onChange={e => setForm({...form, department: e.target.value})}/></Field>
                <Field label="Certifications (csv)" className="col-span-2"><DarkInput value={form.certifications} onChange={e => setForm({...form, certifications: e.target.value})}/></Field>
                <details className="col-span-2 border border-[#1E293B] p-3">
                  <summary className="cursor-pointer text-xs text-sky-300">Labour profile (optional)</summary>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <Field label="Employment group"><DarkInput value={form.employment_group} onChange={e => setForm({...form, employment_group: e.target.value})}/></Field>
                    <Field label="Labour group"><GroupSelect value={form.labor_group_id} groups={groups} onChange={value => setForm({...form, labor_group_id: value})}/></Field>
                    <Field label="Classifications (csv)"><DarkInput value={form.labor_classifications} onChange={e => setForm({...form, labor_classifications: e.target.value})}/></Field>
                    <Field label="Ratings (csv)"><DarkInput value={form.ratings} onChange={e => setForm({...form, ratings: e.target.value})}/></Field>
                  </div>
                </details>
              </div>
              <DialogFooter><Button data-testid="btn-save-personnel" onClick={create} className="bg-sky-600 hover:bg-sky-500 rounded-none">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="panel p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            <th className="text-left pb-2">Name</th><th className="text-left pb-2">Role</th><th className="text-left pb-2">Labour profile</th><th className="text-left pb-2">Certifications</th><th className="text-left pb-2">Status</th><th/>
          </tr></thead>
          <tbody>{visiblePeople.map(person => (
            <tr key={person.id} className="border-b border-[#1E293B] hover:bg-[#0E1522]" data-testid={`personnel-row-${person.id}`}>
              <td className="py-2"><User size={12} className="inline mr-1 text-sky-400"/>{person.name}</td>
              <td className="py-2 text-slate-400">{person.role}</td>
              <td className="py-2 text-xs text-slate-500">{groupById[person.labor_group_id]?.name || (person.employment_group === "union_longshore" ? <span className="text-amber-400">Group not configured</span> : person.employment_group)}</td>
              <td className="font-mono py-2 text-[10px] text-slate-500">{(person.certifications || []).join(", ")}</td>
              <td>{canManageReference ? <button data-testid={`toggle-personnel-${person.id}`} onClick={() => toggleStatus(person)} className={`font-mono text-[10px] uppercase px-2 py-0.5 ${person.status === "available" ? "sev-ok" : "sev-warning"}`}>{person.status}</button> : <span className={`font-mono text-[10px] uppercase px-2 py-0.5 ${person.status === "available" ? "sev-ok" : "sev-warning"}`}>{person.status}</span>}</td>
              <td className="text-right"><Button size="sm" variant="ghost" className="text-sky-300" onClick={() => openProfile(person)}>Details</Button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <Dialog open={Boolean(selected)} onOpenChange={isOpen => !isOpen && setSelected(null)}>
        <DialogContent className="bg-[#0E1522] border-[#1E293B] rounded-none text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && <>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><BriefcaseBusiness size={17}/>{selected.name} — labour profile</DialogTitle></DialogHeader>
            <div className="flex gap-2 border border-amber-700/50 bg-amber-950/20 p-3 text-xs text-amber-200">
              <ShieldAlert size={16} className="shrink-0"/><span>This view may contain confidential labour history. Authentication and access controls are required before deployment.</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Employment group"><DarkInput value={selected.employment_group || ""} onChange={e => setSelected({...selected, employment_group: e.target.value})}/></Field>
              <Field label="Labour group"><GroupSelect value={selected.labor_group_id} groups={groups.filter(group => !group.terminal_id || group.terminal_id === selected.terminal_id)} onChange={value => setSelected({...selected, labor_group_id: value})}/></Field>
              <Field label="Classifications (csv)"><DarkInput value={selected.labor_classifications} onChange={e => setSelected({...selected, labor_classifications: e.target.value})}/></Field>
              <Field label="Ratings (csv)"><DarkInput value={selected.ratings} onChange={e => setSelected({...selected, ratings: e.target.value})}/></Field>
              <Field label="Employer"><DarkInput value={selected.employer || ""} onChange={e => setSelected({...selected, employer: e.target.value})}/></Field>
              <Field label="Dispatch identifier"><DarkInput value={selected.dispatch_identifier || ""} onChange={e => setSelected({...selected, dispatch_identifier: e.target.value})}/></Field>
              <Field label="Seniority date"><DarkInput type="date" value={selected.seniority_date} onChange={e => setSelected({...selected, seniority_date: e.target.value})}/></Field>
              <label className="mt-5 flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={Boolean(selected.regular_workforce)} onChange={e => setSelected({...selected, regular_workforce: e.target.checked})}/>Regular workforce</label>
            </div>
            {canManageReference && <Button onClick={saveProfile} size="sm" className="w-fit bg-sky-600 rounded-none">Save profile</Button>}

            <div className="border-t border-[#1E293B] pt-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><h3 className="font-semibold">Cases and decisions</h3><p className="text-xs text-slate-500">Only reviewed, active cases affect operational conflict checks. {summary?.agreements?.length || 0} linked agreement(s) · {summary?.active_rules?.length || 0} active rule(s).</p></div>
                <span className="text-xs text-slate-500">Activation and resolution are attributed to the signed-in reviewer.</span>
              </div>
              <div className="mt-3 space-y-2">
                {(summary?.cases || []).map(caseItem => <details key={caseItem.id} className="border border-[#1E293B] p-3">
                  <summary className="cursor-pointer text-sm">
                    <span className={caseItem.status === "active" ? "text-amber-300" : "text-slate-300"}>{caseItem.title}</span>
                    <span className="ml-2 font-mono text-[10px] uppercase text-slate-500">{caseItem.archived ? "archived · " : ""}{caseItem.status} · v{caseItem.version || 1}</span>
                  </summary>
                  <p className="mt-2 text-xs text-slate-400">{caseItem.summary || caseItem.message}</p>
                  <div className="mt-3 flex gap-2">
                    {caseItem.status === "proposed" && !caseItem.archived && <Button size="sm" onClick={() => activateCase(caseItem)} className="bg-emerald-700 rounded-none">Activate after review</Button>}
                    {caseItem.status === "active" && <Button size="sm" onClick={() => resolveCase(caseItem)} className="bg-slate-700 rounded-none">Resolve</Button>}
                    {!caseItem.archived && ["active", "proposed"].includes(caseItem.status) && <Button size="sm" variant="outline" onClick={() => reviseCase(caseItem)} className="border-[#1E293B] rounded-none">Propose revision</Button>}
                  </div>
                </details>)}
                {!summary?.cases?.length && <p className="text-xs text-slate-500">No case history recorded.</p>}
              </div>
              <details className="mt-3 border border-[#1E293B] p-3">
                <summary className="cursor-pointer text-sm text-sky-300">{caseParent ? `Revise ${caseParent.title}` : "Add a case or decision"}</summary>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Field label="Title" className="col-span-2"><DarkInput value={caseForm.title} onChange={e => setCaseForm({...caseForm, title: e.target.value})}/></Field>
                  <Field label="Type"><DarkInput value={caseForm.case_type} onChange={e => setCaseForm({...caseForm, case_type: e.target.value})}/></Field>
                  <Field label="Severity">
                    <Select value={caseForm.severity} onValueChange={value => setCaseForm({...caseForm, severity: value})}><SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue/></SelectTrigger><SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none"><SelectItem value="info">Info</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="blocking">Blocking</SelectItem></SelectContent></Select>
                  </Field>
                  <Field label="Summary" className="col-span-2"><DarkInput value={caseForm.summary} onChange={e => setCaseForm({...caseForm, summary: e.target.value})}/></Field>
                  <Field label="Operational message" className="col-span-2"><DarkInput value={caseForm.message} onChange={e => setCaseForm({...caseForm, message: e.target.value})}/></Field>
                  <Field label="Effective from"><DarkInput type="datetime-local" value={caseForm.effective_from} onChange={e => setCaseForm({...caseForm, effective_from: e.target.value})}/></Field>
                  <Field label="Effective to"><DarkInput type="datetime-local" value={caseForm.effective_to} onChange={e => setCaseForm({...caseForm, effective_to: e.target.value})}/></Field>
                  <details className="col-span-2 border border-[#1E293B] p-3">
                    <summary className="cursor-pointer text-xs text-sky-300">Source and operational detail</summary>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <Field label="Linked agreement">
                        <Select value={caseForm.agreement_id || "__none"} onValueChange={value => setCaseForm({...caseForm, agreement_id: value === "__none" ? "" : value})}><SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue/></SelectTrigger><SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none"><SelectItem value="__none">Not linked</SelectItem>{(summary?.agreements || []).map(item => <SelectItem key={item.id} value={item.id}>{item.title}</SelectItem>)}</SelectContent></Select>
                      </Field>
                      <Field label="Required action"><DarkInput value={caseForm.required_action} onChange={e => setCaseForm({...caseForm, required_action: e.target.value})}/></Field>
                      <Field label="Source / file reference" className="col-span-2"><DarkInput value={caseForm.source_reference} onChange={e => setCaseForm({...caseForm, source_reference: e.target.value})}/></Field>
                      <Field label="Private details" className="col-span-2"><textarea value={caseForm.details} onChange={e => setCaseForm({...caseForm, details: e.target.value})} rows={3} className="w-full bg-[#050B14] border border-[#1E293B] p-2 text-sm"/></Field>
                    </div>
                  </details>
                </div>
                <div className="mt-3 flex gap-2"><Button size="sm" onClick={createCase} className="bg-sky-600 rounded-none">Save inactive proposal</Button>{caseParent && <Button size="sm" variant="outline" onClick={() => { setCaseParent(null); setCaseForm(caseDraft()); }} className="border-[#1E293B] rounded-none">Cancel revision</Button>}</div>
              </details>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DarkInput({ className = "", ...props }) {
  return <Input {...props} className={`bg-[#050B14] border-[#1E293B] rounded-none ${className}`}/>;
}

function TerminalSelect({ value, terms, onChange }) {
  return <Select value={value} onValueChange={onChange}><SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue placeholder="Select..."/></SelectTrigger><SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">{terms.map(term => <SelectItem key={term.id} value={term.id}>{term.name}</SelectItem>)}</SelectContent></Select>;
}

function GroupSelect({ value, groups, onChange }) {
  return <Select value={value || "__none"} onValueChange={next => onChange(next === "__none" ? "" : next)}><SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue placeholder="Not linked"/></SelectTrigger><SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none"><SelectItem value="__none">Not linked</SelectItem>{groups.filter(group => group.active !== false).map(group => <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>)}</SelectContent></Select>;
}

function Field({ label, children, className = "" }) {
  return <div className={className}><Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</Label><div className="mt-1">{children}</div></div>;
}
